"use client";

import { useEffect, useState } from "react";
import { ArrowUp } from "lucide-react";

export function BlogBackToTop({
  label,
  articleId,
  contentId,
  headingId,
}: {
  label: string;
  articleId: string;
  contentId: string;
  headingId: string;
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const article = document.getElementById(articleId);
    const content = document.getElementById(contentId);
    if (!article || !content) return;

    let frame = 0;
    const updateVisibility = () => {
      frame = 0;
      const viewportHeight = window.innerHeight;
      const articleTop = article.getBoundingClientRect().top + window.scrollY;
      const isLongArticle = content.offsetHeight > viewportHeight * 1.5;
      const isPastIntro = window.scrollY > articleTop + viewportHeight * 0.75;
      setIsVisible(isLongArticle && isPastIntro);
    };
    const scheduleUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateVisibility);
    };

    updateVisibility();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleUpdate);
    resizeObserver?.observe(content);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      resizeObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [articleId, contentId]);

  const goToTop = () => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    document.getElementById(headingId)?.focus({ preventScroll: true });
  };

  return (
    <button
      type="button"
      onClick={goToTop}
      aria-label={label}
      aria-hidden={!isVisible}
      tabIndex={isVisible ? 0 : -1}
      className={`fixed bottom-4 right-4 z-30 inline-flex h-11 cursor-pointer items-center gap-2 rounded-full border border-border/70 bg-background/90 px-3 text-sm font-medium text-foreground shadow-lg shadow-foreground/10 backdrop-blur transition-[opacity,background-color] duration-200 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:bottom-6 sm:right-6 ${
        isVisible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      <ArrowUp className="size-4" aria-hidden />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
